// Auth-flow storage shim. Three responsibilities:
//   1. No-op for HttpOnly auth tokens (accessToken / refreshToken /
//      mfaAccessToken). The browser holds them; JS cannot read them and
//      must not write them. Legacy callers still pass them through, so
//      the helpers are kept stable.
//   2. Bridge `companyId` between the backend Set-Cookie (login bootstrap)
//      and the canonical localStorage cache held by `tokenStore`.
//   3. Persist short-lived auth-flow values (otpResend, email,
//      emailResend, origin, mfaSetupRequired, ...) to localStorage. Used
//      by AuthenticationHandling / UserHandling / CompanyHandling.
//
// UI preferences (filters, views, page size, theme, locale, sidebar
// state, dashboard selection, etc.) MUST NOT use this module. Use
// `lib/storage/userPrefs` instead.

import { tokenStore } from "@/lib/auth/tokenStore";

export interface CookieApiResponse {
  companyId: string;
}

function emitAuthChanged() {
  try {
    window.dispatchEvent(new Event("veltric:auth-changed"));
  } catch {
    // SSR safety
  }
}

/**
 * Returns the empty string. Auth tokens are HttpOnly cookies the
 * browser sends automatically. Legacy callers thread the result into
 * `Authorization: Bearer ${accessToken}` headers, but the apiShim
 * strips that header before the axios call so the only auth that
 * leaves the cockpit is the HttpOnly cookie set by the backend.
 */
export async function getAccessCookie(): Promise<string> {
  return "";
}

export async function setUncriticalCookie(name: string, value: string, _maxAge?: number): Promise<void> {
  void _maxAge;
  if (name === "companyId") {
    tokenStore.setCompanyId(value);
    emitAuthChanged();
    return;
  }
  if (name === "accessToken" || name === "refreshToken" || name === "mfaAccessToken") {
    // No-op: backend issues these as HttpOnly cookies.
    return;
  }
  try { localStorage.setItem(name, value); } catch { /* SSR / private mode */ }
}

export async function setCriticalCookie(name: string, value: string, _maxAge?: number): Promise<void> {
  return setUncriticalCookie(name, value, _maxAge);
}

function readBrowserCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  try { return decodeURIComponent(match.slice(name.length + 1)); } catch { return null; }
}

export async function getCookie(name: string): Promise<string | null> {
  if (name === "companyId") {
    // localStorage is the canonical store; fall back to a non-HttpOnly
    // cookie so e2e seeders can hand the cockpit a companyId without a
    // navigation (Playwright's addCookies runs before page.goto).
    const ls = tokenStore.getCompanyId();
    if (ls) return ls;
    const fromCookie = readBrowserCookie("companyId");
    if (fromCookie) {
      tokenStore.setCompanyId(fromCookie);
      return fromCookie;
    }
    return null;
  }
  if (name === "accessToken" || name === "refreshToken" || name === "mfaAccessToken") {
    // HttpOnly — invisible to JS.
    return null;
  }
  try { return localStorage.getItem(name); } catch { return null; }
}

export async function setRefreshToken(_refreshToken: string): Promise<void> {
  void _refreshToken;
  // No-op: backend manages refresh cookie.
}

export async function setAccessToken(_accessToken: string): Promise<void> {
  void _accessToken;
  // No-op: backend manages access cookie.
}

export async function removeCookie(name: string): Promise<void> {
  if (name === "companyId") {
    tokenStore.setCompanyId(null);
    emitAuthChanged();
    return;
  }
  if (name === "accessToken" || name === "refreshToken" || name === "mfaAccessToken") {
    // No-op: cookies are cleared by the backend's logout endpoint via Set-Cookie Max-Age=0.
    return;
  }
  try { localStorage.removeItem(name); } catch { /* noop */ }
}
